import * as React from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'wouter';
import { useRecoilValue } from 'recoil';
import {
  ADDRESS_KEY,
  CO2_EMISSION_KEY,
  CUSTOMER_KEY,
  DRIVER_KEY,
  ECO_DRIVE_KEY,
  INFO_FOR_PARKING_KEY,
  INFO_KEY,
  PRIVATE_KEY,
  TIMESTAMP_KEY,
  VEHICLE_KEY,
} from '../constants';
import { URL_ECO_DRIVE, URL_TRIPS_OVERVIEW } from '../../../router/constants';
import CustomerCell from '../TableCell/CustomerCell';
import useTable from '../../CmdTable/hooks/useTable';
import { ITripsOverViewRecord, ITripsOverviewColumn } from '../interfaces';
import { GDPR_VALUE, NOT_AVAILABLE_VALUE, OFF_VALUE, PAID_VALUE } from '../../CmdTable/constants';
import { getLocaleNumber } from '../../../Services/LanguageService';
import { columnsAtom } from '../TripsOverviewState';
import useAnalytics from '../../../hooks/useAnalytics';

interface IAdditionalInformationProps {
  data: ITripsOverViewRecord;
}

function AdditionalInformation(props: IAdditionalInformationProps): JSX.Element {
  const { handleAnonymizedData } = useTable();
  const { formatMessage: f } = useIntl();
  const [, navigate] = useLocation();
  const columnsTable = useRecoilValue<ITripsOverviewColumn[]>(columnsAtom);
  const hiddenColumns = columnsTable.filter((i) => i.hidden && i.key !== INFO_KEY);
  const { trackEvent } = useAnalytics();

  const getElement = (value: string): JSX.Element | null => {
    let element: JSX.Element | null = null;

    if (value === PAID_VALUE || value === OFF_VALUE || value === NOT_AVAILABLE_VALUE) {
      return <>&ndash;</>;
    }
    if (value === GDPR_VALUE) {
      return <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />;
    }

    if (Number(value.replace(',', '.')) < 5) {
      element = (
        <div className="d-flex align-items-center">
          <i className="cmd-icon cmd-icon-eco-drive-bad fs-4 text-c-red mr-2" />
          <strong className="text-c-red">{value}</strong>
        </div>
      );
    } else if (Number(value.replace(',', '.')) < 8) {
      element = (
        <div className="d-flex align-items-center">
          <i className="cmd-icon cmd-icon-eco-drive-not-good fs-4 text-orange-light mr-2" />
          <strong className="text-orange-dark">{value}</strong>
        </div>
      );
    } else {
      element = (
        <div className="d-flex align-items-center">
          <i className="cmd-icon cmd-icon-eco-drive-good fs-4 text-green-light mr-2" />
          <strong className="text-green-dark">{value}</strong>
        </div>
      );
    }
    return element;
  };

  const redirectToMapWithStart = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const startLink = props.data[ADDRESS_KEY].startLink;
    event.preventDefault();
    event.stopPropagation();
    if (startLink) {
      window.open(startLink, '_blank');
      trackEvent(['ride book', 'click', 'Ride book open start address in new tab']);
    }
  };

  const redirectToMapWithEnd = (event: React.MouseEvent<HTMLAnchorElement>) => {
    const endLink = props.data[ADDRESS_KEY].endLink;
    event.preventDefault();
    event.stopPropagation();
    if (endLink) {
      window.open(endLink, '_blank');
      trackEvent(['ride book', 'click', 'Ride book open end address in new tab']);
    }
  };

  const openEcoDriveSubpage = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    const rideId = props.data.id;
    const customerId = props.data[CUSTOMER_KEY].id;
    navigate(
      rideId ? `${URL_TRIPS_OVERVIEW}${URL_ECO_DRIVE}/${customerId}/${rideId}` : URL_TRIPS_OVERVIEW
    );
  };

  const handleTitle = (columnKey: string): string | JSX.Element => {
    if (
      columnKey === ECO_DRIVE_KEY &&
      props.data[columnKey] !== GDPR_VALUE &&
      props.data[columnKey] !== PAID_VALUE &&
      props.data[columnKey] !== OFF_VALUE &&
      props.data[columnKey] !== NOT_AVAILABLE_VALUE
    ) {
      return (
        <a href="" onClick={openEcoDriveSubpage}>
          {f({ id: `tables.tripsOverview.column.${columnKey}` })}
        </a>
      );
    }
    return f({ id: `tables.tripsOverview.column.${columnKey}` });
  };

  const handleData = (columnKey: string): string | JSX.Element | null => {
    if (
      props.data &&
      props.data[columnKey as keyof ITripsOverViewRecord] &&
      columnKey !== INFO_KEY &&
      columnKey !== TIMESTAMP_KEY
    ) {
      switch (columnKey) {
        case CUSTOMER_KEY:
          return <CustomerCell id={String(props.data.id)} isAdditional datacyIndex={0} />;
        case VEHICLE_KEY:
          return props.data[columnKey].name;
        case DRIVER_KEY:
          if (typeof props.data[columnKey].name === 'string') {
            return String(props.data[columnKey].name);
          }
          return <>&ndash;</>;
        case INFO_FOR_PARKING_KEY:
          if (props.data[columnKey]?.duration) {
            const duration = props.data[columnKey]?.duration;
            return duration ? String(duration) : '';
          }
          return f({ id: 'tables.tripsOverview.column.datetime.anonymized' });
        case PRIVATE_KEY:
          return f({ id: 'enum.alarms.ridePrivacy.private' });
        case ECO_DRIVE_KEY:
          return getElement(props.data[columnKey]);
        case CO2_EMISSION_KEY:
          if (
            !props.data[columnKey] ||
            typeof props.data[columnKey].valueCan === 'string' ||
            props.data[columnKey].valueCan === null
          ) {
            return <>&ndash;</>;
          }
          return `${Number(props.data[columnKey].valueCan.toFixed(1)).toLocaleString(getLocaleNumber())} kg`;
        default:
          return handleAnonymizedData(props.data[columnKey as keyof ITripsOverViewRecord]);
      }
    }

    if (columnKey === PRIVATE_KEY) {
      return f({ id: 'enum.alarms.ridePrivacy.public' });
    }

    return <i className="cmd-icons cmd-icon-gdpr-document-lock-dark-gray" />;
  };

  return (
    <div className="w-waypoints-timeline">
      <div className="font-weight-bold mb-2">
        {f({ id: 'tripsOverview.additionalInformation.title' })}
      </div>
      {hiddenColumns && (
        <div className="row no-gutters b-table-prehlad-jazd-extra-info pb-3">
          {hiddenColumns.length === 0 ? (
            <div className="col-6">
              {f({ id: 'tables.tripsOverview.table.detailRow.additionalInfo' })}
            </div>
          ) : (
            hiddenColumns.map((column: ITripsOverviewColumn) =>
              column.key === ADDRESS_KEY ? (
                [
                  <div className="col-6 mt-2 pr-3">
                    <div className="b-table-prehlad-jazd-extra-info-title">
                      {props.data[column.key].startLink ? (
                        <a href="" onClick={redirectToMapWithStart}>
                          {f({ id: 'tripsOverview.modal.rideStart' })}
                        </a>
                      ) : (
                        f({ id: 'tripsOverview.modal.rideStart' })
                      )}
                    </div>
                    <div className="b-table-prehlad-jazd-extra-info-content">
                      {props.data[column.key].startLink
                        ? props.data[column.key].start
                        : f({ id: 'tables.tripsOverview.column.datetime.anonymized' })}
                    </div>
                  </div>,
                  <div className="col-6 mt-2 pr-3">
                    <div className="b-table-prehlad-jazd-extra-info-title">
                      {props.data[column.key].endLink ? (
                        <a href="" onClick={redirectToMapWithEnd}>
                          {f({ id: 'tripsOverview.modal.rideEnd' })}
                        </a>
                      ) : (
                        f({ id: 'tripsOverview.modal.rideEnd' })
                      )}
                    </div>
                    <div className="b-table-prehlad-jazd-extra-info-content">
                      {props.data[column.key].endLink
                        ? props.data[column.key].end
                        : f({ id: 'tables.tripsOverview.column.datetime.anonymized' })}
                    </div>
                  </div>,
                ]
              ) : (
                <div className="col-6 mt-2 pr-3">
                  <div className="b-table-prehlad-jazd-extra-info-title">
                    {handleTitle(column.key)}
                  </div>
                  <div className="b-table-prehlad-jazd-extra-info-content">
                    {handleData(column.key)}
                  </div>
                </div>
              )
            )
          )}
        </div>
      )}
    </div>
  );
}

export default AdditionalInformation;
